/**
 * Baby Development Activities Page
 * Manage play activities for a phase
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, GripVertical, Pencil, Trash2, Check, X, Clock, Baby } from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Toggle } from '@/components/ui/Toggle';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';
import type { BabyDevelopmentPhase, BabyDevelopmentActivity } from './types';

const CATEGORY_OPTIONS = [
  { value: 'motorik', label: 'Motorik' },
  { value: 'sinne', label: 'Sinne' },
  { value: 'kommunikation', label: 'Kommunikation' },
  { value: 'sozial', label: 'Sozial' },
];

export function BabyDevelopmentActivitiesPage() {
  const supabase = getSupabaseAdminClient();
  const { id: phaseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState({ title: '', description: '', duration: '', category: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [newData, setNewData] = useState({ title: '', description: '', duration: '', category: '' });

  // Fetch phase
  const { data: phase } = useQuery({
    queryKey: ['baby-development-phase', phaseId, contentLocale],
    queryFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const { data, error } = await (supabase as any)
        .from('baby_development_phases')
        .select('*')
        .eq('id', phaseId)
        .eq('locale', contentLocale)
        .single();
      if (error) throw error;
      return data as BabyDevelopmentPhase;
    },
    enabled: !!phaseId,
  });

  // Fetch activities
  const { data: activities, isLoading } = useQuery({
    queryKey: ['baby-development-activities', phaseId, contentLocale],
    queryFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const { data, error } = await (supabase as any)
        .from('baby_development_activities')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('locale', contentLocale)
        .order('sort_order');
      if (error) throw error;
      return data as BabyDevelopmentActivity[];
    },
    enabled: !!phaseId,
  });

  // Add activity
  const addMutation = useMutation({
    mutationFn: async () => {
      if (!phaseId) throw new Error('No phase ID');
      const maxOrder = activities?.reduce((max, a) => Math.max(max, a.sort_order), 0) ?? 0;
      const { error } = await (supabase as any)
        .from('baby_development_activities')
        .insert({
          phase_id: phaseId,
          title: newData.title,
          description: newData.description,
          duration: newData.duration || null,
          category: newData.category || null,
          sort_order: maxOrder + 1,
          locale: contentLocale,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Aktivität hinzugefügt');
      queryClient.invalidateQueries({ queryKey: ['baby-development-activities', phaseId, contentLocale] });
      setIsAdding(false);
      setNewData({ title: '', description: '', duration: '', category: '' });
    },
    onError: (error) => {
      toast.error('Fehler: ' + (error as Error).message);
    },
  });

  // Update activity
  const updateMutation = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await (supabase as any)
        .from('baby_development_activities')
        .update({
          title: editData.title,
          description: editData.description,
          duration: editData.duration || null,
          category: editData.category || null,
        })
        .eq('id', activityId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Aktivität aktualisiert');
      queryClient.invalidateQueries({ queryKey: ['baby-development-activities', phaseId, contentLocale] });
      setEditingId(null);
    },
    onError: (error) => {
      toast.error('Fehler: ' + (error as Error).message);
    },
  });

  // Toggle active
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ activityId, isActive }: { activityId: string; isActive: boolean }) => {
      const { error } = await (supabase as any)
        .from('baby_development_activities')
        .update({ is_active: isActive })
        .eq('id', activityId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baby-development-activities', phaseId, contentLocale] });
    },
  });

  // Delete activity
  const deleteMutation = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await (supabase as any)
        .from('baby_development_activities')
        .delete()
        .eq('id', activityId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Aktivität gelöscht');
      queryClient.invalidateQueries({ queryKey: ['baby-development-activities', phaseId, contentLocale] });
    },
  });

  // Move activity
  const moveMutation = useMutation({
    mutationFn: async ({ activityId, direction }: { activityId: string; direction: 'up' | 'down' }) => {
      if (!activities) return;

      const currentIndex = activities.findIndex(a => a.id === activityId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (targetIndex < 0 || targetIndex >= activities.length) return;

      const current = activities[currentIndex];
      const target = activities[targetIndex];

      await (supabase as any)
        .from('baby_development_activities')
        .update({ sort_order: target.sort_order })
        .eq('id', current.id);

      await (supabase as any)
        .from('baby_development_activities')
        .update({ sort_order: current.sort_order })
        .eq('id', target.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baby-development-activities', phaseId, contentLocale] });
    },
  });

  const startEditing = (activity: BabyDevelopmentActivity) => {
    setEditingId(activity.id);
    setEditData({
      title: activity.title,
      description: activity.description,
      duration: activity.duration || '',
      category: activity.category || '',
    });
  };

  // Early return after all hooks
  if (!phaseId) {
    return <div className="text-center py-12 text-slate-500">Phase nicht gefunden</div>;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/features/baby-development')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
            <Baby className="w-6 h-6 text-teal-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Aktivitäten: {phase?.display_range ?? ''}</h1>
            <p className="text-slate-500">Phase {phase?.phase_number}: "{phase?.subtitle}"</p>
          </div>
          <LocaleSelector />
        </div>
      </div>

      {/* Add Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Aktivität hinzufügen
        </button>
      </div>

      {/* Add Form */}
      {isAdding && (
        <div className="bg-white rounded-lg shadow-sm p-4 border-2 border-teal-200">
          <div className="space-y-4">
            <input
              type="text"
              value={newData.title}
              onChange={(e) => setNewData({ ...newData, title: e.target.value })}
              placeholder="Titel der Aktivität"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              autoFocus
            />
            <RichTextEditor
              value={newData.description}
              onChange={(html) => setNewData({ ...newData, description: html })}
              placeholder="Beschreibung"
              minHeight="100px"
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={newData.duration}
                onChange={(e) => setNewData({ ...newData, duration: e.target.value })}
                placeholder="Dauer (z.B. 5-10 Min)"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
              <select
                value={newData.category}
                onChange={(e) => setNewData({ ...newData, category: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Kategorie wählen</option>
                {CATEGORY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewData({ title: '', description: '', duration: '', category: '' });
                }}
                className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Abbrechen
              </button>
              <button
                onClick={() => addMutation.mutate()}
                disabled={!newData.title.trim() || !newData.description.trim() || addMutation.isPending}
                className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                Hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activities List */}
      <div className="bg-white rounded-lg shadow-sm divide-y">
        {activities?.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            Noch keine Aktivitäten vorhanden
          </div>
        )}

        {activities?.map((activity, index) => (
          <div
            key={activity.id}
            className={`p-4 ${!activity.is_active ? 'bg-slate-50' : ''}`}
          >
            {editingId === activity.id ? (
              // Edit mode
              <div className="space-y-3">
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  autoFocus
                />
                <RichTextEditor
                  value={editData.description}
                  onChange={(html) => setEditData({ ...editData, description: html })}
                  placeholder="Beschreibung"
                  minHeight="100px"
                />
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={editData.duration}
                    onChange={(e) => setEditData({ ...editData, duration: e.target.value })}
                    placeholder="Dauer"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                  <select
                    value={editData.category}
                    onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Kategorie wählen</option>
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => updateMutation.mutate(activity.id)}
                    disabled={!editData.title.trim() || !editData.description.trim()}
                    className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    <Check className="w-4 h-4" />
                    Speichern
                  </button>
                </div>
              </div>
            ) : (
              // Display mode
              <div className="flex items-start gap-4">
                {/* Drag Handle */}
                <div className="flex flex-col gap-1 pt-1">
                  <button
                    onClick={() => moveMutation.mutate({ activityId: activity.id, direction: 'up' })}
                    disabled={index === 0}
                    className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
                  >
                    <GripVertical className="w-4 h-4 text-slate-400 rotate-90" />
                  </button>
                  <button
                    onClick={() => moveMutation.mutate({ activityId: activity.id, direction: 'down' })}
                    disabled={index === (activities?.length ?? 0) - 1}
                    className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
                  >
                    <GripVertical className="w-4 h-4 text-slate-400 -rotate-90" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`font-medium ${!activity.is_active ? 'text-slate-400' : 'text-slate-900'}`}>
                      {activity.title}
                    </h3>
                    {activity.duration && (
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        {activity.duration}
                      </span>
                    )}
                    {activity.category && (
                      <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                        {CATEGORY_OPTIONS.find(c => c.value === activity.category)?.label}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{activity.description}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3">
                  <Toggle
                    checked={activity.is_active}
                    onChange={(checked) => toggleActiveMutation.mutate({
                      activityId: activity.id,
                      isActive: checked,
                    })}
                    label="Aktiv"
                  />
                  <button
                    onClick={() => startEditing(activity)}
                    className="p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Aktivität wirklich löschen?')) {
                        deleteMutation.mutate(activity.id);
                      }
                    }}
                    className="p-2 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
